import React, { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useHistory } from 'react-router'
import { Box, capitalize, CircularProgress, MenuItem, Select, Skeleton, Stack } from '@mui/material'
import Grid from '@mui/material/Grid'

import { useStores } from '@trivie/core'
import { Header } from '@trivie/ui-web/components/header'
import { Insight } from '@trivie/ui-web/components/insight'
import { Punchcard } from '@trivie/ui-web/components/punchcard'
import { Leaderboard } from '@trivie/ui-web/components/leaderboard'
import { DateFilter } from '@trivie/ui-web/components/date-filter'
import { GenerateReport } from '@trivie/ui-web/components/generate-report'
import { Panel } from '@trivie/ui-web/components/panel/panel'
import { ArrowDropDownRounded } from '@mui/icons-material'
import { color } from '@trivie/ui-web/theme'
import { Text } from '@trivie/ui-web/components/text'
import { DateTime } from 'luxon'
import { ReportsApi } from '@trivie/core/src/services/api-objects'

export const EngagementScreen: React.FunctionComponent = observer(() => {
  const history = useHistory()
  const { reportStore, manageLeaderboardStore, manageUserStore } = useStores()
  const { userProfile } = manageUserStore
  const { getBaseStats, getHourlyActivity, baseStats, hourlyActivity } = reportStore
  const { groupLeaderboard, userLeaderboard, getLeaderboardBase } = manageLeaderboardStore
  const [loading, setLoading] = useState(true)

  const [timespan, setTimespan] = useState('All Time')
  const [userStatus, setUserStatus] = useState('all')
  const [start, setStart] = useState<any>(DateTime.local(1971).startOf('day').toISO())
  const [end, setEnd] = useState<any>(DateTime.local().endOf('day').toISO())

  const [loadingBaseStats, setLoadingBaseStats] = useState(true)
  const [loadingHourly, setLoadingHourly] = useState(true)
  const [loadingUserLeaderboard, setLoadingUserLeaderboard] = useState(true)
  const [loadingGroupLeaderboard, setLoadingGroupLeaderboard] = useState(true)

  const baseStatsRef = useRef<any>()
  const hourlyRef = useRef<any>()
  const groupLeaderboardRef = useRef<any>()
  const userLeaderboardRef = useRef<any>()

  useEffect(() => {
    return () => {
      cancelRequests()
    }
  }, [])

  const cancelRequests = () => {
    if (baseStatsRef.current) {
      baseStatsRef.current.cancel()
    }
    if (hourlyRef.current) {
      hourlyRef.current.cancel()
    }
    if (userLeaderboardRef.current) {
      userLeaderboardRef.current.cancel()
    }
    if (groupLeaderboardRef.current) {
      groupLeaderboardRef.current.cancel()
    }
  }

  const load = async (startDateTime: string, endDateTime: string, status: string) => {
    cancelRequests()

    setLoadingBaseStats(true)
    setLoadingHourly(true)
    setLoadingUserLeaderboard(true)
    setLoadingGroupLeaderboard(true)

    const baseStatsCancel = ReportsApi.getCancelToken()
    baseStatsRef.current = baseStatsCancel
    getBaseStats(startDateTime, endDateTime, status, baseStatsCancel.token)
      .then(() => setLoadingBaseStats(false))
      .catch(() => setLoadingBaseStats(true))

    const hourlyCancel = ReportsApi.getCancelToken()
    hourlyRef.current = hourlyCancel
    getHourlyActivity(startDateTime, endDateTime, status, hourlyCancel.token)
      .then(() => setLoadingHourly(false))
      .catch(() => setLoadingHourly(true))

    const userLeaderboardCancel = ReportsApi.getCancelToken()
    userLeaderboardRef.current = userLeaderboardCancel
    getLeaderboardBase(startDateTime, endDateTime, false, status, userLeaderboardCancel.token)
      .then(() => setLoadingUserLeaderboard(false))
      .catch(() => setLoadingUserLeaderboard(true))

    const groupLeaderboardCancel = ReportsApi.getCancelToken()
    groupLeaderboardRef.current = groupLeaderboardCancel
    getLeaderboardBase(startDateTime, endDateTime, true, status, groupLeaderboardCancel.token)
      .then(() => setLoadingGroupLeaderboard(false))
      .catch(() => setLoadingGroupLeaderboard(true))
  }

  const onDateChange = (startDateTime, endDateTime, label) => {
    setStart(startDateTime)
    setEnd(endDateTime)
    setTimespan(label as any)
    load(startDateTime, endDateTime, userStatus)
  }

  const onUserStatusChange = (e) => {
    setUserStatus(e.target.value)
    load(start, end, e.target.value)
  }

  const showMore = () => history.push('/insights/leaderboards')

  const gdpr_enabled = userProfile?.workspace_settings?.gdpr_enabled

  return (
    <>
      <Header divider title="Engagement" />
      <Stack mb={2} direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" gap={1}>
          <DateFilter onDateChange={onDateChange} />
          <Select
            sx={{ '& .MuiSelect-select:focus': { backgroundColor: 'transparent' } }}
            autoWidth
            renderValue={(value) => (
              <Text
                variant="labelMedium"
                text={`${capitalize(value)} Users`}
                style={{ color: color.shade70 }}
              />
            )}
            disableUnderline
            variant="standard"
            value={userStatus}
            IconComponent={ArrowDropDownRounded}
            onChange={onUserStatusChange}
          >
            <MenuItem value={'all'}>All Users</MenuItem>
            <MenuItem value={'active'}>Active Users</MenuItem>
            <MenuItem value={'deactivated'}>Deactivated Users</MenuItem>
          </Select>
        </Stack>
        <GenerateReport filename="engagement" />
      </Stack>
      <Grid container spacing={2} pb="40px">
        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 8 }} spacing={3}>
          <Stack flex={1} height="100%" useFlexGap gap={2}>
            {!loadingBaseStats ? (
              <Panel title="How many users have recent activity?">
                <Stack direction="row" flexWrap="wrap">
                  <Insight
                    noAveraging
                    unit=" Users"
                    title="Recently Active Users"
                    value={baseStats?.active_users}
                    data={baseStats?.stats_history[0].data_points}
                    timespan={timespan}
                  />
                  <Insight
                    noAveraging
                    unit=" Users"
                    title="Total Users"
                    value={baseStats?.total_users}
                    data={baseStats?.stats_history[1].data_points}
                    timespan={timespan}
                  />
                </Stack>
              </Panel>
            ) : (
              <Box minHeight={165} sx={{ width: '100%' }}>
                <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
              </Box>
            )}

            {!loadingBaseStats ? (
              <Panel title="What are they accomplishing?">
                <Stack direction="row" flexWrap="wrap">
                  <Insight
                    noAveraging
                    title="Questions Answered"
                    unit=" Questions"
                    value={baseStats?.questions_answered}
                    data={baseStats?.stats_history[2].data_points}
                    timespan={timespan}
                  />
                  <Insight
                    noAveraging
                    unit=" Quizzes"
                    title="Quizzes Completed"
                    value={baseStats?.quizzes_completed}
                    data={baseStats?.stats_history[3].data_points}
                    timespan={timespan}
                  />
                </Stack>
              </Panel>
            ) : (
              <Box minHeight={165} sx={{ width: '100%' }}>
                <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
              </Box>
            )}

            {loadingHourly ? (
              <Box minHeight={500} sx={{ width: '100%' }}>
                <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
              </Box>
            ) : (
              <Panel title="How much time are users spending on Trivie?" divider>
                <Punchcard values={hourlyActivity} />
              </Panel>
            )}
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 4 }}>
          <Stack flex={1} height="100%" useFlexGap gap={2}>
            {!gdpr_enabled &&
              (loadingUserLeaderboard ? (
                <Box minHeight={500} sx={{ width: '100%' }}>
                  <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
                </Box>
              ) : (
                <Leaderboard hideExport showMore={showMore} type="user" data={userLeaderboard} />
              ))}
            {loadingGroupLeaderboard ? (
              <Box minHeight={500} sx={{ width: '100%' }}>
                <Skeleton sx={{ transform: 'none', height: '100%', borderRadius: '20px' }} />
              </Box>
            ) : (
              <Leaderboard hideExport showMore={showMore} type="group" data={groupLeaderboard} />
            )}
          </Stack>
        </Grid>
      </Grid>
    </>
  )
})
